import * as React from 'react';
import { useThemeConfig } from '@resi-media/resi-ui';
import { produce } from 'immer';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import flatten from 'lodash/flatten';
import ReactGA from 'react-ga4';
import { createContainer } from 'react-tracked';
import useSWR from 'swr';
import { LIVE_PLAYER_SITES_POLL_INTERVAL } from '@sites/constants/live-player';
import type CustomError from '@sites/helpers/customError';
import type { Playlists, Sites } from '@sites/types';
import { useEnvironment } from '../environmentContext';

enum FEED_CONTEXT_ACTION_TYPES {
  SET_VIDEOS,
}

type VideoAction = {
  payload: Playlists.Derived.Video[];
  type: FEED_CONTEXT_ACTION_TYPES.SET_VIDEOS;
};

const feedContextReducer = (state: FeedContextState, action: VideoAction) => {
  switch (action.type) {
    case FEED_CONTEXT_ACTION_TYPES.SET_VIDEOS:
      state.videosMap = state.videosMap.set(action.payload[0].playlistId, action.payload);
      break;
  }
};

type FeedContextState = {
  videosMap: Map<string, Playlists.Derived.Video[]>;
};

type FeedContextHook = FeedContextState & {
  error?: CustomError;
  getPlaylist: (playlistId?: string) => Sites.Get.Playlist | undefined;
  getVideo: (videoId?: string, playlistId?: string) => Playlists.Derived.Video | undefined;
  playlists?: Sites.Get.Playlist[];
  setVideos: (v: Playlists.Derived.Video[]) => void;
  site?: Sites.Get.Site;
  tokenData?: { jwt: string };
  tokenError?: CustomError;
  videos: Playlists.Derived.Video[];
};

const curriedReducerFunction = produce(feedContextReducer);

const useValues = (): readonly [FeedContextHook, () => void] => {
  const customerSite = window.location.pathname.split('/')[1];
  const { SITES_API_V1, sitesClientUrl } = useEnvironment();
  const ldClient = useLDClient();
  const { setMode } = useThemeConfig();
  const url = `${SITES_API_V1}/sites?url=${sitesClientUrl}${customerSite}`;
  const tokenUrl = `${SITES_API_V1}/token?url=${sitesClientUrl}${customerSite}`;
  const { data, error } = useSWR<Sites.Get.Site, CustomError>([url], null, {
    refreshInterval: LIVE_PLAYER_SITES_POLL_INTERVAL,
  });
  const { data: tokenData, error: tokenError } = useSWR<{ jwt: string }>([tokenUrl], null, {
    revalidateOnFocus: false,
    refreshInterval: ({ jwt } = { jwt: '' }) => {
      if (!jwt) return 0;
      const jwtValue = JSON.parse(atob(jwt.split('.')[1]));
      const refreshInterval = (jwtValue.exp - jwtValue.iat) * 1000;

      return refreshInterval;
    },
  });

  const [{ videosMap }, dispatch] = React.useReducer(curriedReducerFunction, {
    videosMap: new Map(),
  });

  React.useEffect(() => {
    if (ldClient && customerSite) {
      ldClient.identify({ kind: 'slug', key: customerSite });
    }
  }, [customerSite, ldClient]);

  React.useEffect(() => {
    if (data?.googleAnalyticsId) {
      ReactGA.initialize(data.googleAnalyticsId);
      ReactGA.send('pageview');
    }
  }, [data?.googleAnalyticsId]);

  React.useEffect(() => {
    if (data) {
      setMode(data.branding?.mode === 'light' ? 'light' : 'dark');
    }

    if (data?.name && document.title !== data.name) {
      document.title = data.name;
    }

    if (data?.description) {
      const metaName = 'description';
      let metaDescription: HTMLMetaElement | null = document.querySelector(`meta[name="${metaName}"]`);

      if (metaDescription === null) {
        metaDescription = document.createElement('meta');
        metaDescription.name = metaName;
        document.head.appendChild(metaDescription);
      }

      if (metaDescription.content !== data.description) {
        metaDescription.content = data.description;
      }
    }
  }, [data, setMode]);

  const setVideos = React.useCallback(
    (newVideos: Playlists.Derived.Video[]) => {
      if (!videosMap.has(newVideos[0].playlistId)) {
        dispatch({ payload: newVideos, type: FEED_CONTEXT_ACTION_TYPES.SET_VIDEOS });
      }
    },
    [videosMap]
  );

  const getPlaylist = React.useCallback(
    (playlistId?: string) => {
      if (!playlistId || !data) return;

      return data.feeds[0].playlists.find((x) => x.id === playlistId);
    },
    [data]
  );

  const getVideo = React.useCallback(
    (videoId?: string, playlistId?: string) => {
      if (!videoId || !playlistId || !videosMap.size) return;
      return flatten([...videosMap.values()]).find((x) => x.id === videoId && x.playlistId === playlistId);
    },
    [videosMap]
  );

  const feed = React.useMemo(() => {
    return {
      ...(data && { site: data }),
      ...(data?.feeds[0]?.playlists && { playlists: data.feeds[0]?.playlists.filter((playlist) => playlist.size > 0) }),
      error,
      videos: flatten([...videosMap.values()]),
      videosMap,
      getPlaylist,
      getVideo,
      setVideos,
      tokenData,
      tokenError,
    };
  }, [data, error, getPlaylist, getVideo, setVideos, tokenData, tokenError, videosMap]);

  return [
    feed,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: FeedProvider,
  useTrackedState: useFeed,
  useUpdate: useFeedUpdate,
} = createContainer<FeedContextHook, () => void, { children: React.ReactNode }>(useValues);

export { FeedProvider, useFeed, useFeedUpdate };
export type { FeedContextHook };
